import { useEffect, useRef, useState } from "react";

/**
 * Animates a number from 0 to `target` once, with easing.
 *
 * Used by the Home command centre for its headline figures. Returns the
 * current display value, so a component can render {useCountUp(total)}.
 *
 * - Honours prefers-reduced-motion: returns the target immediately.
 * - Driven by requestAnimationFrame against elapsed time, not a fixed tick,
 *   so the duration is the same regardless of frame rate.
 * - Re-runs when the target changes (e.g. when the fetch resolves), which is
 *   the common case here: 0 -> real value once data lands.
 */
export default function useCountUp(target, duration = 900) {
  const safeTarget = Number.isFinite(Number(target)) ? Number(target) : 0;
  const [value, setValue] = useState(0);
  const frameRef = useRef();

  useEffect(() => {
    if (safeTarget === 0) {
      setValue(0);
      return undefined;
    }

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      setValue(safeTarget);
      return undefined;
    }

    const start = performance.now();
    // easeOutCubic — fast out of the gate, settles gently on the number
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      setValue(Math.round(safeTarget * ease(progress)));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
      }
    };

    frameRef.current = requestAnimationFrame(step);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [safeTarget, duration]);

  return value;
}
